import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Upload, Image as ImageIcon, Video, Trash2, AlertCircle, 
  Crown, Sparkles, X, Check, Loader, Edit2
} from 'lucide-react';
import { getImageUrl } from '../utils/imageUrl';
import { readFile, createCroppedImage } from '../utils/cropImage';
import ProfilePictureCropModal from './ProfilePictureCropModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MediaGallery = () => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [showCropModal, setShowCropModal] = useState(false);
  const [selectedImageForCrop, setSelectedImageForCrop] = useState(null);
  const [limits, setLimits] = useState({
    max_gallery_images: 5,
    max_gallery_videos: 0,
    max_image_size_mb: 5,
    max_video_size_mb: 0
  });

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/provider/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setProfile(response.data);
      
      // Set limits based on plan_level
      const planLevel = response.data.plan_level || 'basic';
      if (planLevel === 'pro') {
        setLimits({
          max_gallery_images: 10,
          max_gallery_videos: 1,
          max_image_size_mb: 5,
          max_video_size_mb: 100
        });
      } else if (planLevel === 'plus') {
        setLimits({
          max_gallery_images: 10,
          max_gallery_videos: 0,
          max_image_size_mb: 5,
          max_video_size_mb: 0
        });
      } else {
        setLimits({
          max_gallery_images: 5,
          max_gallery_videos: 0,
          max_image_size_mb: 5,
          max_video_size_mb: 0
        });
      }
      
    } catch (error) {
      console.error('Error loading profile:', error);
      toast.error('Fehler beim Laden des Profils');
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file size
    const fileSizeMB = file.size / (1024 * 1024);
    if (fileSizeMB > limits.max_image_size_mb) {
      toast.error(`Datei zu groß. Maximum: ${limits.max_image_size_mb} MB`);
      return;
    }

    // Validate file type (MIME + extension fallback for HEIC/AVIF)
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif', 'image/bmp', 'image/tiff', 'image/heic', 'image/heif', 'image/avif'];
    const allowedExtensions = ['jpg', 'jpeg', 'png', 'webp', 'gif', 'bmp', 'tiff', 'tif', 'heic', 'heif', 'avif'];
    const fileExtension = file.name.split('.').pop()?.toLowerCase();
    if (!allowedTypes.includes(file.type) && !allowedExtensions.includes(fileExtension)) {
      toast.error('Ungültiges Dateiformat. Erlaubt: JPG, PNG, WEBP, GIF, BMP, TIFF, HEIC, AVIF');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/provider/upload-gallery-image`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success(`Bild hochgeladen (${response.data.current_count}/${response.data.max_count})`);
      await loadProfile(); // Reload to show new image
    } catch (error) {
      console.error('Upload error:', error);
      const detail = error.response?.data?.detail;

      // Handle structured error response
      if (detail && typeof detail === 'object') {
        console.error('Upload error details:', {
          code: detail.code,
          technical: detail.technical,
          storageInitError: detail.storage_init_error
        });

        // Show user-friendly message based on error code
        if (detail.code === 'STORAGE_NOT_CONFIGURED') {
          toast.error('Speicher nicht verfügbar. Bitte versuche es später erneut oder kontaktiere den Support.');
        } else if (detail.code === 'UPLOAD_FAILED') {
          toast.error(detail.message || 'Fehler beim Hochladen. Bitte versuche es erneut.');
        } else {
          toast.error(detail.message || 'Fehler beim Hochladen');
        }
      } else {
        // Handle string error or fallback
        const errorMsg = typeof detail === 'string' ? detail : 'Fehler beim Hochladen';
        toast.error(errorMsg);
      }
    } finally {
      setUploading(false);
      event.target.value = ''; // Reset input
    }
  };

  const handleVideoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file size
    const fileSizeMB = file.size / (1024 * 1024);
    if (fileSizeMB > limits.max_video_size_mb) {
      toast.error(`Datei zu groß. Maximum: ${limits.max_video_size_mb} MB`);
      return;
    }

    // Validate file type
    if (file.type !== 'video/mp4') {
      toast.error('Ungültiger Dateityp. Erlaubt: MP4');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/provider/upload-gallery-video`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        },
        // For large files, increase timeout
        timeout: 120000 // 2 minutes
      });

      toast.success(`Video hochgeladen (${response.data.current_count}/${response.data.max_count})`);
      await loadProfile();
    } catch (error) {
      console.error('Upload error:', error);
      const detail = error.response?.data?.detail;

      if (detail && typeof detail === 'object') {
        console.error('Upload error details:', {
          code: detail.code,
          technical: detail.technical,
          storageInitError: detail.storage_init_error
        });
        toast.error(detail.message || 'Fehler beim Hochladen');
      } else {
        const errorMsg = typeof detail === 'string' ? detail : 'Fehler beim Hochladen';
        toast.error(errorMsg);
      }
    } finally {
      setUploading(false);
      event.target.value = '';
    }
  };

  const handleDeleteImage = async (imageUrl) => {
    if (!window.confirm('Bild wirklich löschen?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/provider/delete-image`, 
        { url: imageUrl },
        {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      toast.success('Bild gelöscht');
      await loadProfile();
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Fehler beim Löschen');
    }
  };

  const handleDeleteVideo = async (videoUrl) => {
    if (!window.confirm('Video wirklich löschen?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/provider/delete-gallery-video`, 
        { url: videoUrl },
        {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      toast.success('Video gelöscht');
      await loadProfile();
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Fehler beim Löschen');
    }
  };

  const handleProfilePictureUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type (MIME + extension fallback for HEIC/AVIF)
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif', 'image/bmp', 'image/tiff', 'image/heic', 'image/heif', 'image/avif'];
    const allowedExtensions = ['jpg', 'jpeg', 'png', 'webp', 'gif', 'bmp', 'tiff', 'tif', 'heic', 'heif', 'avif'];
    const fileExtension = file.name.split('.').pop()?.toLowerCase();
    if (!allowedTypes.includes(file.type) && !allowedExtensions.includes(fileExtension)) {
      toast.error('Ungültiges Dateiformat. Erlaubt: JPG, PNG, WEBP, GIF, BMP, TIFF, HEIC, AVIF');
      return;
    }

    // Validate file size (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Datei zu groß (max. 10MB)');
      return;
    }

    // Formats that browsers can reliably render in <img> tags (needed for crop modal)
    const croppableTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif', 'image/bmp'];
    const croppableExtensions = ['jpg', 'jpeg', 'png', 'webp', 'gif', 'bmp'];
    const canCrop = croppableTypes.includes(file.type) || croppableExtensions.includes(fileExtension);

    if (canCrop) {
      // Existing flow: read as data URL, open crop modal
      try {
        const imageDataUrl = await readFile(file);
        setSelectedImageForCrop(imageDataUrl);
        setShowCropModal(true);
      } catch (error) {
        console.error('Error reading file:', error);
        toast.error('Fehler beim Laden des Bildes');
      }
    } else {
      // HEIC/AVIF/TIFF: upload directly without cropping (backend handles conversion)
      setUploading(true);
      try {
        const token = localStorage.getItem('token');
        const formData = new FormData();
        formData.append('file', file);

        await axios.post(`${API}/provider/upload-profile-picture`, formData, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          },
          timeout: 30000
        });

        toast.success('Profilbild hochgeladen!');
        await loadProfile();
      } catch (error) {
        console.error('Profile picture upload error:', error);
        const detail = error.response?.data?.detail;
        if (detail && typeof detail === 'object') {
          toast.error(detail.message || 'Fehler beim Hochladen des Profilbilds');
        } else {
          toast.error(typeof detail === 'string' ? detail : 'Fehler beim Hochladen des Profilbilds');
        }
      } finally {
        setUploading(false);
      }
    }

    event.target.value = ''; // Reset input
  };

  const handleCropSave = async (croppedAreaPixels, rotation) => {
    setUploading(true);
    setShowCropModal(false);

    try {
      // Create cropped image
      const croppedImageBlob = await createCroppedImage(
        selectedImageForCrop,
        croppedAreaPixels,
        rotation
      );

      // Validate blob was created successfully
      if (!croppedImageBlob) {
        throw new Error('Bildverarbeitung fehlgeschlagen - bitte versuchen Sie ein anderes Bildformat');
      }

      // Upload cropped image
      const token = localStorage.getItem('token');
      const formData = new FormData();
      // Use correct extension based on blob type (webp or png fallback)
      const extension = croppedImageBlob.type === 'image/png' ? 'png' : 'webp';
      formData.append('file', croppedImageBlob, `profile-picture.${extension}`);

      await axios.post(`${API}/provider/upload-profile-picture`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        },
        timeout: 30000
      });

      toast.success('Profilbild hochgeladen!');
      await loadProfile();
      setSelectedImageForCrop(null);
    } catch (error) {
      console.error('Profile picture upload error:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      const detail = error.response?.data?.detail;

      if (detail && typeof detail === 'object') {
        console.error('Upload error details:', {
          code: detail.code,
          technical: detail.technical,
          storageInitError: detail.storage_init_error
        });
        toast.error(detail.message || 'Fehler beim Hochladen des Profilbilds');
      } else {
        const errorMsg = typeof detail === 'string' ? detail : (error.message || 'Fehler beim Hochladen des Profilbilds');
        toast.error(errorMsg);
      }
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteProfilePicture = async () => {
    if (!window.confirm('Profilbild wirklich löschen?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/provider/delete-profile-picture`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Profilbild gelöscht');
      await loadProfile();
    } catch (error) {
      console.error('Delete profile picture error:', error);
      toast.error('Fehler beim Löschen');
    }
  };

  const getPlanBadge = () => {
    const planLevel = profile?.plan_level || 'basic';
    if (planLevel === 'pro') {
      return (
        <span className="px-3 py-1 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-full text-sm font-semibold flex items-center gap-1">
          <Crown className="w-4 h-4" />
          Pro
        </span>
      );
    } else if (planLevel === 'plus') {
      return (
        <span className="px-3 py-1 bg-gradient-to-r from-[#2B2142] to-[#3D3158] text-white rounded-full text-sm font-semibold flex items-center gap-1">
          <Sparkles className="w-4 h-4" />
          Plus
        </span>
      );
    }
    return (
      <span className="px-3 py-1 bg-gray-200 text-gray-700 rounded-full text-sm font-semibold">
        Basic
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader className="w-8 h-8 animate-spin text-[#2B2142]" />
      </div>
    );
  }

  const images = profile?.images || [];
  const videos = profile?.videos || [];
  const planLevel = profile?.plan_level || 'basic';
  const canUploadImages = images.length < limits.max_gallery_images;
  const canUploadVideos = limits.max_gallery_videos > 0 && videos.length < limits.max_gallery_videos;

  return (
    <div className="space-y-6">
      {/* Header with Plan Badge */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-1">Medien-Galerie</h2>
          <p className="text-sm text-gray-600">
            Verwalte dein Profilbild und deine Portfolio-Medien
          </p>
        </div>
        {getPlanBadge()}
      </div>

      {/* Profile Picture Section */}
      <div className="bg-gradient-to-r from-purple-50 to-[#F4F2F2] border-2 border-purple-200 rounded-2xl p-6">
        <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
          <ImageIcon className="w-5 h-5 text-purple-600" />
          Profilbild / Logo
        </h3>
        <p className="text-sm text-gray-600 mb-4">
          Dieses Bild wird in Suchergebnissen und als Thumbnail angezeigt. Das Bild wird automatisch angepasst und vollständig angezeigt.
        </p>
        
        <div className="flex flex-col sm:flex-row items-start gap-4 sm:gap-6">
          {/* Current Profile Picture */}
          <div className="flex-shrink-0 w-full sm:w-auto flex justify-center sm:justify-start">
            {profile?.profile_picture ? (
              <div className="relative group">
                <div className="w-full max-w-xs sm:w-80 h-32 bg-white rounded-2xl border-4 border-purple-100 shadow-lg overflow-hidden flex items-center justify-center p-3">
                  <img
                    src={getImageUrl(profile.profile_picture)}
                    alt="Profilbild"
                    className="max-w-full max-h-full object-contain"
                    onError={(e) => {
                      console.error('Profile picture load error:', profile.profile_picture);
                      e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="320" height="128"%3E%3Crect fill="%23e5e7eb" width="320" height="128" rx="16"/%3E%3Ctext fill="%239ca3af" x="50%25" y="50%25" text-anchor="middle" dy=".3em" font-size="32"%3ELogo%3C/text%3E%3C/svg%3E';
                    }}
                  />
                </div>
                <button
                  onClick={handleDeleteProfilePicture}
                  className="absolute -top-2 -right-2 bg-red-500 text-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow-lg hover:bg-red-600"
                  type="button"
                  title="Profilbild löschen"
                >
                  <X className="w-4 h-4" />
                </button>
                <button
                  onClick={() => {
                    setSelectedImageForCrop(getImageUrl(profile.profile_picture));
                    setShowCropModal(true);
                  }}
                  className="absolute -bottom-2 left-1/2 -translate-x-1/2 bg-purple-600 text-white px-3 py-1.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow-lg hover:bg-purple-700 flex items-center gap-1.5 text-sm font-semibold"
                  type="button"
                  title="Bild bearbeiten"
                >
                  <Edit2 className="w-3.5 h-3.5" />
                  Bearbeiten
                </button>
              </div>
            ) : (
              <div className="w-full max-w-xs sm:w-80 h-32 bg-gradient-to-br from-gray-100 to-gray-200 rounded-2xl flex items-center justify-center border-4 border-purple-100 shadow-lg">
                <ImageIcon className="w-16 h-16 text-gray-400" />
              </div>
            )}
          </div>
          
          {/* Upload Button */}
          <div className="flex-1 w-full sm:w-auto">
            <label className={`inline-flex w-full sm:w-auto items-center justify-center gap-2 px-6 py-3 rounded-xl font-semibold cursor-pointer transition-all ${
              uploading
                ? 'bg-gray-400 text-white cursor-not-allowed'
                : 'bg-purple-600 hover:bg-purple-700 text-white'
            }`}>
              <Upload className="w-4 h-4" />
              {uploading ? 'Lädt...' : profile?.profile_picture ? 'Profilbild ändern' : 'Profilbild hochladen'}
              <input
                type="file"
                accept="image/jpeg,image/jpg,image/png,image/webp,image/gif,image/bmp,image/tiff,image/heic,image/heif,image/avif"
                onChange={handleProfilePictureUpload}
                className="hidden"
                disabled={uploading}
              />
            </label>
            <p className="text-xs text-gray-500 mt-2 text-center sm:text-left">
              JPG, PNG, WEBP, GIF, BMP, TIFF, HEIC, AVIF bis 10MB • Empfohlen: 800x600px oder größer
            </p>
          </div>
        </div>
      </div>

      {/* Info Box */}
      <div className="bg-[#FFF0D1]/30 border-2 border-[#2B2142]/20 rounded-2xl p-4">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-[#2B2142] flex-shrink-0 mt-0.5" />
          <div className="text-sm text-[#2B2142]">
            <p className="font-semibold mb-1">Deine Portfolio-Limits:</p>
            <ul className="space-y-1">
              <li>• Galerie-Bilder: <strong>{images.length} / {limits.max_gallery_images}</strong></li>
              {limits.max_gallery_videos > 0 && (
                <li>• Videos: <strong>{videos.length} / {limits.max_gallery_videos}</strong></li>
              )}
              <li>• Max. Bildgröße: {limits.max_image_size_mb} MB</li>
              {limits.max_video_size_mb > 0 && (
                <li>• Max. Videogröße: {limits.max_video_size_mb} MB</li>
              )}
            </ul>
          </div>
        </div>
      </div>

      {/* Images Section */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <ImageIcon className="w-5 h-5" />
            Portfolio-Bilder ({images.length}/{limits.max_gallery_images})
          </h3>
          
          {canUploadImages ? (
            <label className={`px-4 py-2 rounded-xl font-semibold cursor-pointer transition-all flex items-center gap-2 ${
              uploading
                ? 'bg-gray-400 text-white cursor-not-allowed'
                : 'bg-[#2B2142] hover:bg-[#1E1730] text-white'
            }`}>
              <Upload className="w-4 h-4" />
              {uploading ? 'Lädt...' : 'Bild hochladen'}
              <input
                type="file"
                accept="image/jpeg,image/jpg,image/png,image/webp,image/gif,image/bmp,image/tiff,image/heic,image/heif,image/avif"
                onChange={handleImageUpload}
                disabled={uploading}
                className="hidden"
              />
            </label>
          ) : (
            <div className="text-sm text-gray-600">
              Limit erreicht
            </div>
          )}
        </div>

        {images.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-2xl border-2 border-dashed border-gray-300">
            <ImageIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-600 mb-2">Noch keine Bilder hochgeladen</p>
            <p className="text-sm text-gray-500">
              Lade bis zu {limits.max_gallery_images} Bilder hoch, um deine Arbeit zu präsentieren
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {images.map((imageSource, index) => (
              <div key={index} className="relative group">
                <img
                  src={getImageUrl(imageSource)}
                  alt={`Gallery ${index + 1}`}
                  className="w-full h-48 object-cover rounded-xl"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="200" height="200"%3E%3Crect fill="%23ddd" width="200" height="200"/%3E%3Ctext fill="%23999" x="50%25" y="50%25" text-anchor="middle" dy=".3em"%3EBild nicht gefunden%3C/text%3E%3C/svg%3E';
                  }}
                />
                <button
                  onClick={() => handleDeleteImage(imageSource)}
                  className="absolute top-2 right-2 p-2 bg-red-600 text-white rounded-lg opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-700"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}

        {!canUploadImages && planLevel === 'basic' && (
          <div className="mt-4 p-4 bg-gradient-to-r from-[#2B2142] to-[#3D3158] rounded-2xl text-white">
            <p className="font-semibold mb-2">Limit erreicht!</p>
            <p className="text-sm text-white/90 mb-3">
              Upgrade auf <strong>Plus</strong> oder <strong>Pro</strong> für mehr Bilder (bis zu 10).
            </p>
            <button
              onClick={() => window.location.href = '/pricing'}
              className="bg-white text-[#2B2142] px-4 py-2 rounded-lg font-semibold hover:bg-[#FFF0D1]/30 transition-colors"
            >
              Jetzt upgraden
            </button>
          </div>
        )}
      </div>

      {/* Videos Section */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <Video className="w-5 h-5" />
            Videos ({videos.length}/{limits.max_gallery_videos})
            {limits.max_gallery_videos === 0 && (
              <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded-full font-normal">
                Pro Feature
              </span>
            )}
          </h3>
          
          {canUploadVideos ? (
            <label className={`px-4 py-2 rounded-xl font-semibold cursor-pointer transition-all flex items-center gap-2 ${
              uploading
                ? 'bg-gray-400 text-white cursor-not-allowed'
                : 'bg-purple-600 hover:bg-purple-700 text-white'
            }`}>
              <Upload className="w-4 h-4" />
              {uploading ? 'Lädt...' : 'Video hochladen'}
              <input
                type="file"
                accept="video/mp4"
                onChange={handleVideoUpload}
                disabled={uploading}
                className="hidden"
              />
            </label>
          ) : limits.max_gallery_videos === 0 ? (
            <div className="text-sm text-gray-600">
              Nur für Pro
            </div>
          ) : (
            <div className="text-sm text-gray-600">
              Limit erreicht
            </div>
          )}
        </div>

        {limits.max_gallery_videos === 0 ? (
          <div className="p-6 bg-gradient-to-r from-purple-500 to-pink-500 rounded-2xl text-white">
            <Crown className="w-12 h-12 mb-3" />
            <h4 className="font-bold text-xl mb-2">Video-Upload mit Pro</h4>
            <p className="text-purple-50 mb-4">
              Lade ein beeindruckendes Video hoch, um deine Arbeit noch besser zu präsentieren.
            </p>
            <button
              onClick={() => window.location.href = '/pricing'}
              className="bg-white text-purple-600 px-6 py-2 rounded-lg font-semibold hover:bg-purple-50 transition-colors"
            >
              Auf Pro upgraden
            </button>
          </div>
        ) : videos.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-2xl border-2 border-dashed border-gray-300">
            <Video className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-600 mb-2">Noch kein Video hochgeladen</p>
            <p className="text-sm text-gray-500">
              Lade 1 Video hoch (max. {limits.max_video_size_mb} MB, MP4-Format)
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {videos.map((videoSource, index) => (
              <div key={index} className="relative group">
                <video
                  src={getImageUrl(videoSource)}
                  controls
                  className="w-full h-64 object-cover rounded-xl bg-black"
                  onError={(e) => {
                    console.error('Video load error:', e);
                  }}
                />
                <button
                  onClick={() => handleDeleteVideo(videoSource)}
                  className="absolute top-2 right-2 p-2 bg-red-600 text-white rounded-lg opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-700"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Crop Modal */}
      {showCropModal && selectedImageForCrop && (
        <ProfilePictureCropModal
          imageUrl={selectedImageForCrop}
          onSave={handleCropSave}
          onClose={() => {
            setShowCropModal(false);
            setSelectedImageForCrop(null);
          }}
        />
      )}
    </div>
  );
};

export default MediaGallery;
